import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { Col } from '@/components/Col';
import { Container as BaseContainer } from '@/components/Container';
import { Flex } from '@/components/Flex';
import type { CompactOnMobileProps } from '@/lib/types';
import { hiddenDown, hiddenUp } from '@/theme';

import { Typography } from '../Typography';

export const Container = styled(BaseContainer)`
  ${({ theme }) => css`
    ${theme.media.md.down} {
      padding: 0;
    }
  `}
`;

export const Wrapper = styled(Flex)`
  ${({ theme }) => css`
    
    ${theme.media.md.up} {
      background-color: ${theme.colors.lightGrey};
      padding: ${theme.spacing(2)} 0 ${theme.spacing(4)}};
    }
  `}
`;

// BEC-24926: these components are shared with the retailer widget, which keeps
// its current sizing, so every change below is gated on $compactOnMobile.

/**
 * The mobile PDP title drops to 1.9rem to free vertical space above the
 * purchase CTA. It sits between h3Mobile (1.75rem) and h2Mobile (2.125rem), so
 * it is a one-off here rather than a new entry in the type scale.
 */
const MOBILE_TITLE_FONT_SIZE = '1.9rem';

/** Price size 'xl' resolves to h3 (2.125rem); -10% to match the image. */
const MOBILE_PRICE_FONT_SIZE = '1.9rem';

/** Financing teasers (Affirm, Oney, Cembra, ...) share one mobile size. */
const MOBILE_FINANCING_FONT_SIZE = '10px';

export const Name = styled.h1<CompactOnMobileProps>`
  font-weight: bold;
  text-transform: uppercase;

  ${({ $compactOnMobile, theme }) => css`
    color: ${theme.colors.black};
    font-size: ${theme.fontSizes.h2};
    font-family: ${theme.fontFamilies.italianPlate};
    padding-bottom: ${theme.spacing(1)};

    ${$compactOnMobile &&
    css`
      ${theme.media.md.down} {
        font-size: ${MOBILE_TITLE_FONT_SIZE};
      }
    `}
  `};
`;

export const PromotedName = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

export const Description = styled(Box)<{ withBorderBottom?: boolean }>`
  line-height: 22px;

  ${({ theme }) => css`
    margin: ${theme.spacing(4, 0, 0, 0)};
    font-size: ${theme.fontSizes.p2};

    ${theme.media.md.up} {
      margin: ${theme.spacing(0, 0, 2, 0)};
      padding-bottom: 0;
    }
  `}
`;

export const Sku = styled.p<CompactOnMobileProps>`
  text-transform: uppercase;

  ${({ $compactOnMobile, theme }) => css`
    padding-bottom: ${theme.spacing(2)};
    font-family: ${theme.fontFamilies.openSans};
    font-size: ${theme.fontSizes.p3};
    color: ${({ theme }) => theme.colors.black};

    ${$compactOnMobile &&
    css`
      /* BEC-24926: the reference number leaves the top of the mobile PDP and
         reappears as the first row of the technical data. Desktop keeps it
         here, above the product title. */
      ${theme.media.md.down} {
        display: none;
      }
    `}
  `}
`;

export const Divider = styled.hr<{ mdHidden?: boolean; mdVisible?: boolean }>`
  opacity: 0.3;
  border: 0;
  height: 1px;
  width: 100%;
  margin: ${({ theme }) => theme.spacing(2)} 0;

  ${({ theme }) => css`
    background: ${theme.colors.quaternaryGrey};
  `}

  ${({ mdHidden }) => mdHidden && hiddenUp('md')}
  ${({ mdVisible }) => mdVisible && hiddenDown('md')}
`;

export const Pricing = styled(Flex)<CompactOnMobileProps>`
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)} 0;

  ${({ $compactOnMobile, theme }) => css`
    ${theme.media.md.down} {
      justify-content: center;

      ${$compactOnMobile &&
      css`
        /* BEC-24926: the padding around the price halves, 16px -> 8px. */
        padding: ${theme.spacing(1)} 0;

        /* The amount comes down by the same 10% as the product image. Price
           size 'xl' resolves to h3 (2.125rem), so -10% is 1.9rem - the same
           value the title lands on. */
        [data-test-id='PriceText'] {
          font-size: ${MOBILE_PRICE_FONT_SIZE};
        }
      `}
    }
  `}
`;

export const DetailsCardCol = styled(Col)`
  ${({ theme }) => css`
    ${theme.media.md.down} {
      padding-top: ${theme.spacing(2)};
    }

    ${theme.media.md.only} {
      grid-column: span 5;
    }
  `}
`;

export const DetailsCard = styled(Box)`
  ${({ theme }) => css`
    ${theme.media.md.up} {
      margin-left: 10%;
      max-width: 440px;
    }

    ${theme.media.lg.up} {
      margin-left: 18%;
      max-width: 440px;
    }

    ${theme.media.md.down} {
      text-align: center;
      padding-inline: ${theme.gridContainer.sm};
    }
  `};
`;

export const BreadcrumbsWrapper = styled(Box)<{
  mdHidden?: boolean;
  mdVisible?: boolean;
}>`
  position: relative;
  display: inline-flex;

  ${({ mdHidden, mdVisible, theme }) => css`
    padding: ${theme.spacing(1, 2)};
    z-index: ${theme.zIndex.navigation};

    ${mdHidden && hiddenUp('md')}
    ${mdVisible && hiddenDown('md')}
    
    ${theme.media.md.up} {
      padding: 0 0 ${theme.spacing(1)};
    }
  `};
`;

export const TaxLabel = styled.span`
  color: ${({ theme }) => theme.colors.secondaryGrey};

  ${({ theme }) => css`
    padding-left: ${theme.spacing(1)};
    font-weight: ${theme.fontWeights.extraLight};
    font-family: ${theme.fontFamilies.italianPlate};
    font-size: ${theme.fontSizes.h5};

    ${theme.media.md.up} {
      font-size: ${theme.fontSizes.h6};
    }
  `}
`;

export const AffirmSentence = styled.p`
  margin-bottom: 15px;
  min-height: 20px;
`;

export const BoutiquePickUp = styled(Flex)<CompactOnMobileProps>`
  justify-content: center;
  padding-bottom: ${({ theme }) => theme.spacing(2)};

  ${({ $compactOnMobile, theme }) => css`
    ${theme.media.md.up} {
      align-items: center;
      justify-content: flex-start;
    }

    ${$compactOnMobile &&
    css`
      /* BEC-24926: the boutique delivery notice comes off the mobile PDP so the
         purchase CTA fits above the fold. Desktop keeps it. */
      ${theme.media.md.down} {
        display: none;
      }
    `}
  `}
`;

/**
 * BEC-24926: wraps the financing teaser group so every provider (Affirm, Zip,
 * Cembra, Oney, Agos, CreditPlus, V12, Orico, Smbc, YourAsset) picks up the
 * reduced mobile type size from one place instead of each owning a copy.
 */
export const FinancingGroup = styled.div<CompactOnMobileProps>`
  ${({ $compactOnMobile, theme }) =>
    $compactOnMobile &&
    css`
      ${theme.media.md.down} {
        /* Every descendant, not only direct children: the providers nest their
           copy to different depths - V12 renders its sentence as a Typography
           inside its own Wrapper, so a direct-child rule left it at 14px. And
           Typography sets its own size, so inheriting from the group would not
           reach it either. The V12 loan calculator is a Modal, which portals
           out of this subtree, so it keeps its own sizing. */
        &,
        & * {
          font-size: ${MOBILE_FINANCING_FONT_SIZE};
        }

        /* BEC-24926: tighten the gap below the financing teaser, 15px -> 8px.
           Referencing the styled component rather than a class or test id so
           the two stay tied together if either moves. */
        ${AffirmSentence} {
          margin-bottom: ${theme.spacing(1)};
        }
      }
    `}
`;

export const LearnMore = styled(Typography)`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-decoration: underline;
  &:hover {
    cursor: pointer;
  }
`;

export const OneySentence = styled(Flex)`
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  &:hover {
    cursor: pointer;
  }
`;

export const Oney3xBadge = styled(Typography)`
  color: ${({ theme }) => theme.colors.white};
  background-color: ${({ theme }) => theme.colors.black};
  border-radius: 50%;
  padding: ${({ theme }) => theme.spacing(0.2, 0.5)};
`;
